import { Site } from '../shared/site';
import { Address } from '../shared/address';
import { Entity, SearchableEntity, Flag } from '../entity';
import { Company } from '../tenant/company';
import { InvoiceContact } from '../invoicing/invoicing.contact';
import { SiteIntegrationMapping } from '../site/site.integration.mapping';
import { MergeCustomerRequest } from './contact.merge.request';
import { EntityStatusType } from '../entity.status/enum';
import { ContactType } from './contact.type';
import { ActivityTag } from '../shared/activity';
import { ActiveWorkOrderResource } from '../workorder.resources/workorder.resource';
import { UserChangePasswordRequest } from '@jms/model';
import { CreditAccountType, CreditOptions, CreditAmount, DueDateType, DueDateOptions, SubContactType, InviteType, AccessType, InvoiceOptions, SchedulingOptions, LoginOptions, DispatchOptions, AccountingOptions, ContactLocation, ParentContact, SocialNetwork, BillToContact, BillToAccount, BillToOptions, WorkBuddyConnectOptions, ConnectLinkStatus, ContactConnectLink, ContactEntity, ContactEntityWithStatus, LoginContactEntity, SmsContactEntity, EmailContactEntity, NotifiableContactEntity, ContactPhotoSettings, TimesheetOptions } from './contact.options';
import { PrimaryContact } from './contact.primary';
import { EntitySubStatus } from '../entity.status/entity.status';
import { CustomFieldMappingEnriched } from '../fields/custom.field';
import * as Enumerable from 'linq';


export const AccountTypes = [
    ContactType.ServiceProvider,
    ContactType.Business,
    ContactType.Individual,
    ContactType.Subcontractor,
    ContactType.Supplier,
    ContactType.WorkBuddyCompany,
    ContactType.Site
];

export const CustomerAccountTypes = [
    ContactType.ServiceProvider,
    ContactType.Business,
    ContactType.Individual,
];

export const AssetCustomerTypes = [
    ...CustomerAccountTypes,
    ContactType.WorkBuddyCompany,
];

export const ContactTypes = [
    ContactType.Employee,
    ContactType.LoginContact,
    ContactType.BasicContact
];

export const LoginContactTypes = [
    ContactType.Employee,
    ContactType.LoginContact,
];

export const CompanyTypes = [
    ContactType.WorkBuddyCompany,
];

export enum ContactGroup {
    Customer = 'Customer',
    Supplier = 'Supplier',
    Invited = 'Invited',
    User = 'User'
}

export enum ContactCounterName {
    ServiceProvider = 'Service Provider',
    Business = 'Business',
    Individual = 'Individual',
    Supplier = 'Supplier',
    Employee = 'Employee',
    Subcontractor = 'Subcontractor',
    Invited = 'Invited'
}

export enum ContactDialogType {
    Edit = 'Edit',
    Add = 'Add',
    Site = 'Site',
    AddBasic = 'Add Basic',
    AddLogin = 'Add Login',
    EditChildContact = 'Edit Child Contact',
    None = 'None',
    InviteSuccess = 'Invite Success',
    ConnectSuccess = 'Connect Success'
}

export enum ContactFilterType {
    AllCustomers = 'All Customers',
    ServiceProvider = 'Service Providers',
    Businesses = 'Businesses',
    Residential = 'Residential',
    Suppliers = 'Suppliers',
    Companies = 'Companies',
    Contractors = 'Contractors',
    AllEmployees = 'All Employees',
    ActiveEmployees = 'Active Employees',
    InvitedEmployees = 'Invited Employees',
    AllContacts = 'All Contacts',
    AllUsers = 'All Users',
    ActiveUsers = 'Active Users',
    InvitedUsers = 'Invited Users',
    Sites = 'Sites',
}

export enum CrmCounterName {
    AllCustomers = 'All Customers',
    ServiceProvider = 'Service Providers',
    Businesses = 'Businesses',
    Residential = 'Residential',
    Suppliers = 'Suppliers',
    Companies = 'Companies',
    Contractors = 'All Contractors',
    Active = 'Active',
    Invited = 'Invited',
    Contacts = 'Contacts',
    Sites = 'Sites'
}


// Please only use in Job - This was created becasue the whole contact was incorreclty being stored in supervisor field
export interface SupervisorEntity extends Entity {
    type: ContactType;
    mobile: string;
    phone: string;
    email: string;
}

export interface Contact extends ContactEntity, NotifiableContactEntity, SearchableEntity {
    legalName?: string;
    type: ContactType;
    subType?: SubContactType;
    firstName?: string;
    lastName?: string;
    businessName?: string;
    phone?: string;
    mobile?: string;
    workPhone?: string;
    email: string;
    username?: string;
    address?: Address;
    contact?: PrimaryContact;
    owner?: Entity;
    primary?: boolean;
    taxId?: string;
    stockLocation?: boolean;

    /**
     * @deprecated
     * Sites are now stored in the sites collection
     */
    sites?: Site[];
    parents?: ParentContact[];
    role?: Entity;
    labels?: Entity[];
    systemLabels?: Entity[];
    workCategories?: Entity[];
    companies?: Entity[];
    scheduling?: SchedulingOptions;
    dispatching?: DispatchOptions;
    invoices?: InvoiceOptions;
    bills?: InvoiceOptions;
    accounting?: AccountingOptions;
    createdBy?: Entity;
    createdAt?: Date;
    updatedAt?: Date;
    updatedBy?: Entity;
    hidePrices?: boolean;
    pricebook?: Entity;
    tags?: ActivityTag[];
    complianceExpiry?: Date;
    newComplianceAdded?: Date;
    password?: string;
    login?: LoginOptions;
    billingAddress?: Address;
    shippingAddress?: Address;
    recentLocation?: ContactLocation;
    recentLocations?: ContactLocation[];
    feedUserId?: string;
    website?: string;
    description?: string;
    title?: string;
    primarySite?: Site;
    credit?: CreditOptions;
    billTo?: BillToOptions;
    financeTags?: Entity[];
    customFields?: any;
    // Transient: populated by portal entity-data strategies (contractor/client)
    // with the Custom Field mappings applicable to this contact for the current
    // portal context. Not persisted to Mongo.
    fields?: CustomFieldMappingEnriched[];
    socialNetwork?: SocialNetwork;
    activeWorkOrder?: ActiveWorkOrderResource;
    fieldImageSettings?: ContactPhotoSettings;
    creditAmount?: CreditAmount;
    timesheet?: TimesheetOptions;
    /** @description credit limit block */
    isBlock?: boolean;
    annualRevenue?: number;
    industry?: string;
    employees?: number;
    zones?: Entity[];
    importId?: string;

    invoiceTheme?: Entity;
    bulkInvoiceTheme?: Entity;
    billTheme?: Entity;
    allowOnlinePayment?: boolean;

    integrationId?: string;
    alternativeEmail?: string; //// This email is using to send password and resend password

    note?: string;

    /** @description job templates */
    defaultServices?: Entity[];

    siteIntegrationMapping?: SiteIntegrationMapping;

    status?: EntitySubStatus;
    emailReminder?: Flag;

    subscriptionTags?: Entity[];
    connectOptions?: WorkBuddyConnectOptions;

    /** @description Settings for type=ServiceProvider only */
    enableSelectAnySite?: boolean;

    /** @description Defines the number of past financial years (1 Jul - 30 Jun) for which jobs will be retrieved. Based on created date of the job */
    clientPortalFinancialYears?: number;
}

export interface ContactSummary extends Entity {
    username?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    mobile?: string;
    workPhone?: string;
    email?: string;
    address?: Address;
    type?: ContactType;
    active?: boolean;
    disabled?: boolean;
    complianceExpiry?: Date;
    subType?: SubContactType;
    scheduling?: SchedulingOptions;
    dispatching?: DispatchOptions;
    billingAddress?: Address;
    bills?: InvoiceOptions;
    billingContacts?: ContactSummary[];
    recentLocation?: ContactLocation;
    timezone?: string;
    //sites?: Site[];
    labels?: any[];
    workCategories?: Entity[];
    title?: string;
    primarySite?: Site;
    accounts?: Entity[];
    parents?: ParentContact[];
    contact?: PrimaryContact;
    billTo?: BillToOptions;
    fieldImageSettings?: ContactPhotoSettings;
    zones?: Entity[];

    invoiceTheme?: Entity;
    billTheme?: Entity;
    accounting?: AccountingOptions;

    invoicingContact?: InvoiceContact;
    credit?: CreditOptions;

    note?: string;
    firstParent?: Entity;
    companies?: Entity[];
    // External system reference for this contact (e.g. the Manhattan ONE supplier reference on a contractor)
    integrationId?: string;
}

export interface ContactGroupedSummary {
    _id: string;
    type?: ContactType;
    name: string;
    phone?: string;
    mobile?: string;
    workPhone?: string;
    email?: string;
    parent?: Entity;
}


export interface CustomerPicker extends ContactSummary {
    salesTaxRate?: Entity;
}

export interface SupplierPicker extends ContactSummary {
    purchaseTaxRate?: Entity;
    markupPercent?: number;
}

export interface SubcontractorPicker extends ContactSummary {
    purchaseTaxRate?: Entity;
}

export interface BillAccount extends ContactSummary {
    purchaseTaxRate?: Entity;
}



export interface ContactInfo {
    _id?: string;
    name?: string;
    address: Address;
    phone: string;
    email: string;
    contactName: string;
    originalAccount?: Entity;
    friendlyName?: string;
}

export interface RecommendedContactSummary extends ContactSummary {
    group: 'Recommended' | 'Out of Zone' | string;
    groupOrder: number;
}

export enum AccountUploadField {
    Type = 'Type',
    AccountName = 'Name',
    LegalName = 'Legal Name',
    TaxID = 'TaxId',
    Phone = 'Phone',
    Email = 'Email',
    Website = 'Website',
    Notes = 'Notes',
    PrimaryContactFirstName = 'Primary Contact First Name',
    PrimaryContactLastName = 'Primary Contact Last Name',
    PrimaryContactMobile = 'Primary Contact Mobile',
    PrimaryContactEmail = 'Primary Contact Email',
    PrimaryContactJobTitle = 'Primary Contact Job Title',
    PrimarySiteStreet = 'Primary Site Street',
    PrimarySiteSuburb = 'Primary Site Suburb',
    PrimarySiteState = 'Primary Site State',
    PrimarySitePostCode = 'Primary Site Post Code'
}

export enum SiteUploadField {
    Name = 'name',
    Street = 'street',
    Suburb = 'suburb',
    State = 'state',
    Postcode = 'postcode',
    ContactFirstName = 'contactFirstName',
    ContactLastName = 'contactLastName',
    ContactMobile = 'contactMobile',
    ContactEmail = 'contactEmail'
}

export const pendingInviteKey: string = 'pendingInvite';

export function getAccountFromCompany(company: Company): Contact {

    const primarySite = {
        _id: company._id,
        name: 'Primary Site',
        system: true
    };

    const contact: Contact = {
        _id: company._id,
        type: ContactType.WorkBuddyCompany,
        name: company.name,
        legalName: company?.legalName ?? company.name,
        phone: company?.phone,
        email: company?.email,
        website: company?.website,
        system: true,
        contact: {
            _id: company._id,
            name: 'Primary Contact',
            firstName: 'Primary ',
            lastName: 'Contact',
            phone: company?.phone,
            email: company?.email,
            system: true
        },
        sites: [
            {
                ...primarySite,
                address: company.address,
                phone: company.phone,
                website: company.website,
            } as Site
        ],
        billTo: {
            company: {
                _id: company._id,
                name: company.name,
            },
            contact: {
                _id: company._id,
                name: `Primary Contact`
            },
            site: {
                ...primarySite
            } as Site
        },
        primarySite: primarySite as Site,
        emailReminder: null,
    };

    return contact;
}

export function getContactFromCompany(company: Company): Contact {
    return {
        _id: company._id,
        type: ContactType.WorkBuddyCompany,
        name: company.name,
        legalName: company.legalName,
        phone: company.phone,
        email: company.email,
        address: company.address,
        website: company.website,
        contact: {
            _id: null,
            type: ContactType.BasicContact,
            firstName: 'Primary',
            lastName: 'Contact',
            phone: company.phone,
            email: company.email,
        } as any,
    }
}

export enum ContactEntityCommandId {
    UpdateStatus = 'Update Status',
    MergeAccount = 'Merge Account',
    Delete = 'Delete',
    ChangePassword = 'Change Password',
    DeleteParentContact = 'DeleteParentContact',
    ConnectInviteIssue = 'ConnectInviteIssue',
    ConnectHandshakeRequest = 'ConnectHandshakeRequest',
    ConnectDisconnect = 'ConnectDisconnect',
    GeocodeSelfHeal = 'GeocodeSelfHeal',
    ViewAuditLog = 'View Audit Log',
}

export type ContactCommands =
    {
        commandId: 'ResendInvite',
        data: {
            contact: Contact;
            parentId: string;
        }
    } |
    {
        commandId: 'DeleteInvite',
        data: {
            contactId: string;
            parentId: string;
        }

    } |
    {
        commandId: ContactEntityCommandId.MergeAccount,
        data: {
            mergeRequest: MergeCustomerRequest;
        }
    } |
    {
        commandId: ContactEntityCommandId.ChangePassword,
        data: {
            changePasswordRequest: UserChangePasswordRequest;
        }
    } |
    {
        commandId: 'DeleteContact',
        data: {
            contactId: string;
            parentId?: string;
        }

    } |
    {
        commandId: ContactEntityCommandId.DeleteParentContact,
        data: {
            contactId: string;
            parentId?: string;
        }

    } |
    {
        commandId: ContactEntityCommandId.ConnectInviteIssue,
        data: {
            contractorContactId: string;
            recipientContactId?: string;
            recipientEmail?: string;
        }
    } |
    {
        commandId: ContactEntityCommandId.ConnectHandshakeRequest,
        data: {
            customerContactId: string;
            inviteCode: string;
        }
    } |
    {
        commandId: ContactEntityCommandId.ConnectDisconnect,
        data: {
            contactId: string;
            reason?: string;
        }
    } |
    {
        commandId: ContactEntityCommandId.GeocodeSelfHeal,
        data: {
            contactId: string;
        }
    };

/**
 * The strict-recommendations match rule — the single truth for "does this contact match the
 * stage's work category and company?". Consumed by getStrictRecommendation (read-side grouping)
 * AND StageResourceReferenceGuard in jms-api (write-side assignment gate), so the picker and the
 * guard cannot drift apart.
 *
 * An empty or missing list on the contact matches nothing — no value does not mean all values.
 * A falsy workCategoryId/companyId likewise matches nothing. forContractor (the contractor-portal
 * staff picker) skips the company axis entirely — its login contacts are matched on work category
 * only.
 */
export function matchesStrictRecommendation(contact: Pick<ContactSummary, 'workCategories' | 'companies'>, workCategoryId: string, companyId: string, forContractor?: boolean): boolean {

    const hasWorkCategory = !!workCategoryId && (contact?.workCategories ?? []).some(item => item?._id === workCategoryId);

    if (forContractor) {
        return hasWorkCategory;
    }

    const hasCompany = !!companyId && (contact?.companies ?? []).some(item => item?._id === companyId);
    return hasWorkCategory && hasCompany;
}

/**
 * A contractor's login contact with no work categories of its own works the contractor's work
 * categories (WBS-13397). Called before matchesStrictRecommendation by the strict auto-dispatch on
 * contractor assign and by the Contractor Portal staff picker, so both admit the same login contacts.
 *
 * This inherits the parent's list; it never widens an empty list to "all categories". A contact
 * with its own categories keeps them, even when they exclude the stage's category.
 */
export function withParentWorkCategories<T extends Pick<ContactSummary, 'workCategories'>>(contact: T, parent: Pick<ContactSummary, 'workCategories'>): T {

    if (contact?.workCategories?.length) {
        return contact;
    }

    return { ...contact, workCategories: parent?.workCategories ?? [] };
}

export function getStrictRecommendation(users: ContactSummary[], workCategoryId: string, companyId: string, zoneIds: string[], forContractor?: boolean): RecommendedContactSummary[] {
    //If stage don't pass a work category, then don't return any resources.
    if (!workCategoryId || !users?.length) {
        return [];
    }

    const recommend = Enumerable.from(users)
        .where(x => !!x?._id)
        .where(x => matchesStrictRecommendation(x, workCategoryId, companyId, forContractor))
        .select((item: RecommendedContactSummary) => {
            const hasZone = (item.zones ?? []).some(x => (zoneIds ?? []).some(z => z == x._id));

            if (hasZone) {
                item.group = 'Recommended';
                item.groupOrder = 1;
            } else {
                item.group = 'Out of Zone';
                item.groupOrder = 2;
            }

            return item;
        })
        .orderBy(x => x.groupOrder)
        .thenBy(x => {
            return x.name;
        })
        .toArray();

    return recommend;
}

export function getRecommendation(items: ContactSummary[], workCategoryId: string, zoneIds: string[], contactType: ContactType): RecommendedContactSummary[] {

    let contractors = Enumerable
        .from(items)
        .where(x => !!x?._id)
        .select((item: RecommendedContactSummary) => {

            const hasWorkCategory = Enumerable.from(item.workCategories ?? []).any(x => x._id === workCategoryId);
            const hasZone = Enumerable.from(item.zones ?? []).any(x => Enumerable.from(zoneIds ?? []).any(z => z == x._id));

            item.group = `${contactType}`;
            item.groupOrder = 3

            if (hasWorkCategory && hasZone) {
                item.group = 'Recommended';
                item.groupOrder = 1;
            } else if (hasWorkCategory && !hasZone) {
                // "Out of Zone": matches the work category but is not in the job's zone.
                // Applies to all contact types, aligning non-strict grouping with strict mode.
                item.group = 'Out of Zone'
                item.groupOrder = 2;
            }

            return item;
        })
        .orderBy(x => x.groupOrder)
        .thenBy(x => {
            return x.name;
        })
        .toArray();

    return contractors;
}

export function isContactDisabled(contact: Contact | ContactEntityWithStatus): boolean {
    // Older employee records carry no status block; a missing status must not read as a crash.
    return !contact?.active || contact.status?.statusType === EntityStatusType.Inactive;
}
